import { useMemo } from 'react';
import { Text, Badge, Stack } from '@mantine/core';
import { format, parseISO, differenceInMonths } from 'date-fns';
import type { Equipment } from '@/types/equipment';
import { CATEGORY_LABELS } from '@/types/equipment';

interface EquipmentTimelineProps {
  equipment: Equipment[];
  onItemClick?: (equipment: Equipment) => void;
}

// Color config by category
const categoryColors: Record<string, { bg: string; border: string; text: string }> = {
  lighting: { bg: 'var(--mantine-color-yellow-1)', border: 'var(--mantine-color-yellow-3)', text: 'var(--mantine-color-yellow-7)' },
  circulation: { bg: 'var(--mantine-color-blue-1)', border: 'var(--mantine-color-blue-3)', text: 'var(--mantine-color-blue-7)' },
  controller: { bg: 'var(--mantine-color-green-1)', border: 'var(--mantine-color-green-3)', text: 'var(--mantine-color-green-7)' },
  automated_testing: { bg: 'var(--mantine-color-teal-1)', border: 'var(--mantine-color-teal-3)', text: 'var(--mantine-color-teal-7)' },
  dosing: { bg: 'var(--mantine-color-cyan-1)', border: 'var(--mantine-color-cyan-3)', text: 'var(--mantine-color-cyan-7)' },
  filtration: { bg: 'var(--mantine-color-grape-1)', border: 'var(--mantine-color-grape-3)', text: 'var(--mantine-color-grape-7)' },
  skimmer: { bg: 'var(--mantine-color-violet-1)', border: 'var(--mantine-color-violet-3)', text: 'var(--mantine-color-violet-7)' },
  heating_cooling: { bg: 'var(--mantine-color-orange-1)', border: 'var(--mantine-color-orange-3)', text: 'var(--mantine-color-orange-7)' },
};

const defaultColor = { bg: 'var(--mantine-color-gray-1)', border: 'var(--mantine-color-gray-3)', text: 'var(--mantine-color-gray-7)' };

export function EquipmentTimeline({ equipment, onItemClick }: EquipmentTimelineProps) {
  const { months, startDate, totalMonths, grouped } = useMemo(() => {
    const allDates = equipment.flatMap((item) =>
      [item.install_date, item.purchase_date, item.created_at]
        .filter(Boolean)
        .map((d) => parseISO(d!))
    );
    const now = new Date();
    allDates.push(now);

    if (allDates.length <= 1) {
      // No dates available, use current year
      const start = new Date(now.getFullYear(), 0, 1);
      return { months: [], startDate: start, totalMonths: 12, grouped: [] };
    }

    const earliest = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const start = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
    const total = differenceInMonths(now, start) + 2;

    const monthLabels: { label: string; date: Date }[] = [];
    for (let i = 0; i < total; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      monthLabels.push({ label: format(d, 'MMM yy'), date: d });
    }

    // Group by category
    const categoryMap = new Map<string, Equipment[]>();
    for (const item of equipment) {
      const cat = item.category || 'other';
      if (!categoryMap.has(cat)) categoryMap.set(cat, []);
      categoryMap.get(cat)!.push(item);
    }

    const grouped = Array.from(categoryMap.entries()).map(([category, items]) => ({
      category,
      label: CATEGORY_LABELS[category] || category,
      items,
    }));

    return { months: monthLabels, startDate: start, totalMonths: total, grouped };
  }, [equipment]);

  const getBarStyle = (item: Equipment) => {
    const startDateStr = item.install_date || item.purchase_date || item.created_at;
    const addedMonths = differenceInMonths(parseISO(startDateStr), startDate);
    const endDate = item.status === 'retired' ? parseISO(item.updated_at) : new Date();
    const durationMonths = differenceInMonths(endDate, parseISO(startDateStr)) + 1;

    const left = `${(Math.max(0, addedMonths) / totalMonths) * 100}%`;
    const width = `${(Math.max(durationMonths, 1) / totalMonths) * 100}%`;
    return { left, width };
  };

  if (equipment.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No equipment to display in timeline
      </Text>
    );
  }

  return (
    <Stack gap="xl">
      {grouped
        .filter((g) => g.items.length > 0)
        .map((group) => {
          const colors = categoryColors[group.category] || defaultColor;
          return (
            <div key={group.category}>
              <Text size="sm" fw={600} mb="xs">
                {group.label}
              </Text>
              <div style={{ position: 'relative', overflowX: 'auto' }}>
                {/* Month header */}
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                  marginBottom: 4,
                  minWidth: 800,
                }}>
                  {months.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        width: `${100 / totalMonths}%`,
                        fontSize: 10,
                        color: 'var(--mantine-color-dimmed)',
                        flexShrink: 0,
                      }}
                    >
                      {i % 3 === 0 ? m.label : ''}
                    </div>
                  ))}
                </div>

                {/* Bars */}
                <div style={{ minWidth: 800 }}>
                  {group.items.map((item) => {
                    const style = getBarStyle(item);
                    const isRetired = item.status === 'retired';
                    return (
                      <div
                        key={item.id}
                        style={{
                          position: 'relative',
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 4,
                        }}
                      >
                        {/* Grid lines */}
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          pointerEvents: 'none',
                        }}>
                          {months.map((_, i) => (
                            <div
                              key={i}
                              style={{
                                borderLeft: '1px solid var(--mantine-color-default-border)',
                                opacity: 0.2,
                                height: '100%',
                                flexShrink: 0,
                                width: `${100 / totalMonths}%`,
                              }}
                            />
                          ))}
                        </div>
                        {/* Bar */}
                        <div
                          style={{
                            position: 'absolute',
                            height: 24,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 8,
                            paddingRight: 8,
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: `1px solid ${colors.border}`,
                            backgroundColor: colors.bg,
                            color: colors.text,
                            opacity: isRetired ? 0.5 : 1,
                            left: style.left,
                            width: style.width,
                            minWidth: 80,
                            cursor: onItemClick ? 'pointer' : undefined,
                          }}
                          title={`${item.name} — ${
                            item.install_date ? format(parseISO(item.install_date), 'MMM yyyy') : 'Unknown'
                          } → ${isRetired ? 'Retired' : 'Present'}`}
                          onClick={onItemClick ? () => onItemClick(item) : undefined}
                        >
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {item.name}
                          </span>
                          {isRetired && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color="red"
                              style={{ flexShrink: 0 }}
                            >
                              Retired
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
    </Stack>
  );
}
